/**
 * LiveStreamingPage — the community live-session screen (web port of Flutter
 * `live_streaming_screen.dart`). One page serves BOTH roles off the hook's
 * `isBroadcaster` branch (broadcaster publishes + controls; audience view-only).
 * Page owns the route-state snapshot, the `screenKey` mint, the Agora
 * client/provider, and the `useBasePage` lifecycle (forwarding the hook's
 * `onPageInit` / `onPageDispose`); `useLiveStreaming` owns the Agora
 * orchestration, the community-wall Firestore side-effects, the token lifecycle,
 * and the liveness subscription. Route args (`location.state`) carry the full
 * live context — NO by-id fetch (like `MediaDetailsPage` / `VideoCallPage`).
 *
 * The page OWNS every toast/sheet the hook chain surfaces (the hooks stay
 * UI-free, Task 9/10 architecture Must Fix): it supplies `onJoinError` /
 * `onTokenRenewError` wired to `toast.error(...)` (mirroring
 * `CommunityPage.handleLikeMessageError`) and opens the end-live confirm sheet.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import AgoraRTC, { AgoraRTCProvider } from 'agora-rtc-react';
import type { IAgoraRTCClient } from 'agora-rtc-react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { SendGiftSheet } from '@/presentation/feature/gift/components/SendGiftSheet';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useBeforeUnload } from '@/presentation/utils/tabVisibility';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';

import type { RootState } from '@/presentation/store';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import { SendGiftTargetType } from '@/domain/enums/gift/sendGiftTargetType';

import { useLiveStreaming } from './hooks/useLiveStreaming';
import { useLiveStreamingChat } from './hooks/useLiveStreamingChat';
import { LiveStreamingVideoView } from './components/LiveStreamingVideoView';
import { LiveStreamingControls } from './components/LiveStreamingControls';
import { LiveStreamingStateViews } from './components/LiveStreamingStateViews';
import { LiveStreamingWatchersStat } from './components/LiveStreamingWatchersStat';
import { LiveStreamingCoinsStat } from './components/LiveStreamingCoinsStat';
import { LiveStreamingChatOverlay } from './components/LiveStreamingChatOverlay';
import type { LiveStreamingRouteArgs } from './args/liveStreamingRouteArgs';

/**
 * Build the unread-pill preview string from the NEWEST unread item, keyed by
 * type (Flutter `unreadMessageWidget`, `live_streaming_screen.dart:1642-1656`):
 * text → the raw body; gift/photo/video/live → the `liveStreamingPage.unread.*`
 * templates keyed on the item's author name. `null` when the bucket is empty.
 * Mirrors `CommunityPage.tsx`'s inline builder but off the DISTINCT live
 * templates — a module-level pure fn (not an inline component block) so the body
 * stays under the 300-line threshold.
 *
 * The `live` branch uses the CORRECTED "started a Live streaming" template;
 * Flutter's source has the typo `"streming"` (`live_streaming_screen.dart:1655`),
 * so the web spells it correctly. Bug found on Flutter, web implemented
 * correctly; follow-up recommended to fix Flutter bug.
 */
const buildLiveUnreadPreview = (
  unreadItems: CommunityChatMessageEntity[],
  unread: { gift: string; photo: string; video: string; live: string },
): string | null => {
  if (unreadItems.length === 0) return null;
  const lastUnread = unreadItems[unreadItems.length - 1];
  const authorName = lastUnread.messageUsersInfo.userName;
  switch (lastUnread.type) {
    case CommunityChatMessageType.text:
      return lastUnread.text;
    case CommunityChatMessageType.image:
      return unread.photo.replace('{name}', authorName);
    case CommunityChatMessageType.video:
      return unread.video.replace('{name}', authorName);
    case CommunityChatMessageType.gift:
      return unread.gift.replace('{name}', authorName);
    default:
      // `live` and any future type → the corrected Live-streaming template.
      return unread.live.replace('{name}', authorName);
  }
};

/**
 * Inner body — runs INSIDE `<AgoraRTCProvider>` so `useLiveStreaming`
 * (`useLiveStreamingAgora` → `useRTCClient()`) and the video view can consume the
 * Agora hooks. Owns the hook, the `useBasePage` lifecycle bridge, and the
 * rendered surface.
 */
const LiveStreamingPageBody: React.FC<{
  args: LiveStreamingRouteArgs;
  screenKey: string;
  currentUserFirebaseId: string;
}> = ({ args, screenKey, currentUserFirebaseId }) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const { formatCallDuration } = useDateFormatters();

  const copy = l10n.community.liveStreamingPage;

  // Localized live/end copy is built HERE — the presentation layer owns l10n +
  // date-formatting; `useLiveStreaming` stays l10n-free and takes the finished
  // strings (finding 6).
  // `"{name} is now Live"` (`:607`), interpolated once for the start-live write.
  const liveText = useMemo(
    () => copy.isNowLive.replace(/\{name\}/g, args.broadcasterName),
    [copy.isNowLive, args.broadcasterName],
  );
  // `"{name} was live for {duration}"` (`:694`) — the hook computes the live
  // duration on dispose and hands it here so the friendly `formatCallDuration`
  // string is built in presentation.
  const buildEndText = useCallback(
    (liveDurationMs: number): string =>
      copy.wasLiveFor
        .replace(/\{name\}/g, args.broadcasterName)
        .replace(/\{duration\}/g, formatCallDuration(liveDurationMs)),
    [copy.wasLiveFor, args.broadcasterName, formatCallDuration],
  );

  // The single place the liveStreaming feature touches routing. Passed into the
  // hook so its terminal paths (initial-token failure, token-regen failure) pop
  // without the hook reaching into the Navigator (Task 9/10 keeps the hooks
  // UI-free). Stable via `useCallback` so the hook's dep arrays do not churn.
  const goBack = useCallback((): void => Navigator.goBack(), []);

  // Page-owned terminal-error toasts (the hooks stay UI-free, Task 9/10 Must
  // Fix) — the exact `CommunityPage.handleLikeMessageError` pattern.
  const onJoinError = useCallback((): void => {
    toast.error(copy.couldNotJoinToast);
  }, [copy]);
  const onTokenRenewError = useCallback((): void => {
    toast.error(copy.tokenRegenFailedToast);
  }, [copy]);

  const liveStreaming = useLiveStreaming({
    broadcasterFirebaseId: args.broadcasterFirebaseId,
    broadcasterName: args.broadcasterName,
    broadcasterStreamingId: args.broadcasterStreamingId,
    liveStreamingMessageId: args.liveStreamingMessageId,
    liveText,
    buildEndText,
    goBack,
    onJoinError,
    onTokenRenewError,
  });

  // Page-owned offline-send toast (the hook stays UI-free, Task 9/10 Must Fix) —
  // the SAME `toast.error` pattern as `onJoinError` above. Fired by the chat hook
  // via `onOfflineSend` when a send hits the internet guard
  // (`live_streaming_screen.dart:768-771`).
  const onOfflineSend = useCallback((): void => {
    toast.error(copy.noInternetToast);
  }, [copy]);

  // Live-chat orchestration. The load fires from the channel-connected trigger
  // (`channelConnected = !isOpeningChannel`) mirroring Flutter running the chat
  // load from `onJoinChannelSuccess` (`live_streaming_screen.dart:443-444`), NOT
  // at init.
  const chat = useLiveStreamingChat({
    liveStreamingMessageId: args.liveStreamingMessageId,
    broadcasterFirebaseId: args.broadcasterFirebaseId,
    channelConnected: !liveStreaming.isOpeningChannel,
    onOfflineSend,
  });

  // Unread-pill preview — the newest unread item formatted by type (see the
  // module-level `buildLiveUnreadPreview`, Flutter `:1642-1656`). `null` when the
  // bucket is empty.
  const unreadPreview = useMemo<string | null>(
    () => buildLiveUnreadPreview(chat.unreadItems, copy.unread),
    [chat.unreadItems, copy.unread],
  );

  // Last-gift banner title — "You sent a gift" when the current user sent it,
  // "{userName} sent a gift" otherwise (Flutter `:930-934, :980`). The PAGE owns
  // the current-user decision + the l10n lookup and hands the finished string to
  // the overlay (mirrors `VideoCallPage`'s `isCurrentUserSender` decision,
  // `VideoCallPage.tsx:252-253`). Cheap plain derivation — no `useMemo` needed.
  const lastGiftTitle = copy.sentGift.replace(
    '{name}',
    liveStreaming.latestLiveGift?.userId === currentUserFirebaseId
      ? copy.you
      : (liveStreaming.latestLiveGift?.userName ?? ''),
  );

  // Audience gift-tap → open the send-gift sheet in its live-streaming variant.
  // Flutter builds an `AppTransaction` and calls
  // `NavigationService.showSendGiftSheet` (`live_streaming_screen.dart:1419-1430`);
  // on the web the sheet carries the `liveStreaming` target and the Task-3 service
  // assembles the `broadcastingInfo` transaction on confirm, so the page only
  // supplies the broadcaster identity + the live message id. The PAGE owns the
  // sheet open (hooks/components never open sheets, `workflow/lessons.md`); it
  // reuses the SAME `l10n.gift.sendGift` title `VideoCallPage` passes. Opening the
  // sheet naturally blurs the composer (Flutter's `_focusNode.unfocus()`, `:1431`).
  const handleGiftTap = useCallback(
    (): void =>
      SendGiftSheet.show(
        {
          kind: SendGiftTargetType.liveStreaming,
          broadcasterFirebaseId: args.broadcasterFirebaseId,
          broadcasterName: args.broadcasterName,
          liveStreamingMessageId: args.liveStreamingMessageId,
        },
        l10n.gift.sendGift,
        undefined,
        screenKey,
      ),
    [args, screenKey, l10n.gift.sendGift],
  );

  // Bumped when the page is re-shown via back (a pushed child route popped).
  // LiveStreamingPage is kept alive (route caching), and the reversed chat
  // VirtualList scrolls in its own inner container (not the page scroll parent),
  // so it loses its position while hidden and resets to the top (oldest) on
  // re-show. Flutter's didPopNext is a no-op (live_streaming_screen.dart:267-272)
  // because its ListView preserves scroll; this web-only restore snaps the list
  // back to the newest message.
  const [scrollToNewestToken, setScrollToNewestToken] = useState(0);

  // Page-only `useBasePage` forwarding the hook's run-once `onPageInit` (Flutter
  // `init` :201-217) and `onPageDispose` (Flutter `dispose` / `disposeStreaming`
  // :219-254). `onPageInit` returns the init-side teardown `useBasePage` runs on
  // unmount.
  useBasePage({
    screenKey,
    onInit: (): (() => void) => {
      // TODO: @claude add a follow up task for this: screen wake lock (KeepScreenOn parity) — Flutter live_streaming_screen.dart:202,221
      return liveStreaming.onPageInit();
    },
    // Web-only scroll-restore on return-via-back (a pushed child route popped).
    // NOT a pill re-raise — the unread-pill re-raise stays unimplemented because
    // Flutter's `didPopNext` (`:267-272`) is a no-op and the app-resume re-raise
    // is already covered by the hook's tab-visibility (business-parity review_0
    // Should Fix 1). Flutter's no-op reflects that its `ListView` preserves scroll
    // across route transitions; the web `VirtualList`'s own inner scroll box does
    // NOT, so on re-show it resets to the top (oldest). Bump the restore token so
    // the overlay snaps the reversed list back to the newest message.
    onPopNext: (): void => {
      setScrollToNewestToken((t) => t + 1);
    },
    // Compose BOTH feature hooks' dispose. `liveStreaming.onPageDispose` tears down
    // the Agora session / liveness subscription; `chat.onPageDispose` tears down
    // the live-message subscription + the unread-pill timer
    // (`live_streaming_screen.dart:225-229`).
    onDispose: (): void => {
      liveStreaming.onPageDispose();
      chat.onPageDispose();
    },
  });

  // `useBasePage.onDispose` fires on React unmount (SPA nav) but NOT when the
  // browser tab is closed/reloaded/navigated-away — so the session cleanup
  // (audience watcher −1, broadcaster end-live write, subscription cancel,
  // Agora teardown) would otherwise leak on tab close. `beforeunload` covers
  // exactly those cases and — unlike `visibilitychange` — does NOT fire on mere
  // backgrounding, which the user requires must not leave the session. The
  // dispose is idempotent (`hasDisposedRef`), so a reload firing both surfaces
  // does not double-decrement. Best-effort during unload (the Firestore write
  // may not always flush before the tab dies) — see finding_3 reliability note.
  useBeforeUnload(() => liveStreaming.onPageDispose());

  // Coarse page-root status token for the qa-tester (precedence mirrors the
  // Flutter Stack branching, `live_streaming_screen.dart:861-873`).
  const pageStatus = liveStreaming.streamingEnded
    ? 'ended'
    : liveStreaming.isRenewingToken
      ? 'renewingToken'
      : liveStreaming.isOpeningChannel
        ? 'loading'
        : 'live';

  // Shared end-live confirm gate (Flutter `:1595-1610`): show the confirm sheet,
  // and only on a confirmed (truthy) response leave + pop via `goBack` (dispose
  // fires the end write). Both the bottom control-column end-live button and the
  // top-bar back arrow (broadcaster) route through this — the sheet-opening lives
  // on the page (Task 9/10 keeps controls presentational).
  const confirmThenEndLive = async (): Promise<void> => {
    const response = await ConfirmSheet.show({
      title: copy.endConfirmTitle,
      confirmText: copy.endConfirmDescription,
      okText: l10n.common.yes,
      cancelText: l10n.common.cancel,
      screenKey,
    });
    if (response) {
      goBack();
    }
  };

  // Top-bar back arrow. The audience back pops directly (view-only). A
  // broadcaster back ENDS the session, so it gates on the same confirm the
  // end-live button uses — unless the session is already over (mirror
  // `VideoCallPage:122-131`).
  const handleBack = async (): Promise<void> => {
    if (!liveStreaming.isBroadcaster || liveStreaming.streamingEnded) {
      goBack();
      return;
    }
    await confirmThenEndLive();
  };

  return (
    <div
      {...qaAttr({ id: 'live-streaming-page', status: pageStatus })}
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: theme.background,
      }}
    >
      {/* Video layer — hidden once ended / while opening (Flutter `videoView()`
          gated `!_liveStreamingEnded && !_isOpeningChannel`, `:861-862`). */}
      {!liveStreaming.streamingEnded && !liveStreaming.isOpeningChannel && (
        <LiveStreamingVideoView
          isBroadcaster={liveStreaming.isBroadcaster}
          localCameraTrack={liveStreaming.localCameraTrack}
          remoteUsers={liveStreaming.remoteUsers}
          broadcasterStreamingId={liveStreaming.broadcasterStreamingId}
          clearScreen={liveStreaming.clearScreen}
          onToggleClearScreen={liveStreaming.toggleClearScreen}
        />
      )}

      {/* Top gradient scrim band, hidden when cleared (Flutter `topOverlay` :863). */}
      {!liveStreaming.clearScreen && (
        <LiveStreamingStateViews variant="topOverlay" />
      )}

      {/* Loading avatar while the channel opens (Flutter `loadingWidget` :864). */}
      {liveStreaming.isOpeningChannel && (
        <LiveStreamingStateViews
          variant="loading"
          broadcasterFirebaseId={args.broadcasterFirebaseId}
        />
      )}

      {/* Live chat overlay — the bottom band Flutter draws over the video
          (`listWithReplyActions`, `live_streaming_screen.dart:892-920`), hidden
          while `_clearScreen || _liveStreamingEnded || _isOpeningChannel`
          (`:893`). It positions itself absolutely (`bottom: 0`) with an explicit
          `z-index: 5`: it sits OVER the video + top scrim yet UNDER the
          broadcaster controls (z 6) + top-bar band (z 10) — i.e. "under the top
          bar, over the video". The explicit z-index (paired with
          `isolation: 'isolate'` on the video surface, which contains the Agora
          player's internal stacking context) makes the overlay the deterministic
          pointer-event owner in its band, so audience taps on the composer /
          message list no longer fall through to the clear-screen video toggle. */}
      {!liveStreaming.clearScreen &&
        !liveStreaming.streamingEnded &&
        !liveStreaming.isOpeningChannel && (
          <LiveStreamingChatOverlay
            messages={chat.messages}
            isFirstLoad={chat.isFirstLoad}
            unreadPreview={unreadPreview}
            unreadBannerVisible={chat.unreadBannerVisible}
            isScrolledUp={chat.isScrolledUp}
            unreadItemsCount={chat.unreadItems.length}
            scrollToTopToken={chat.scrollToTopToken}
            scrollToNewestToken={scrollToNewestToken}
            onSendText={chat.sendText}
            onScrolledToBottom={chat.onScrolledToBottom}
            onScrolledAway={chat.onScrolledAway}
            onLoadMore={chat.onLoadMore}
            lastGift={liveStreaming.latestLiveGift}
            lastGiftOpacity={liveStreaming.latestLiveGiftOpacity}
            lastGiftTitle={lastGiftTitle}
            isBroadcaster={liveStreaming.isBroadcaster}
            onGiftTap={handleGiftTap}
          />
        )}

      {/* Broadcaster control column — live surface only (Flutter `_streamActions`
          gated `_isBroadcaster && !_clearScreen && !_liveStreamingEnded &&
          !_isOpeningChannel`, `:865-866`). Positions itself absolutely. */}
      {liveStreaming.isBroadcaster &&
        !liveStreaming.clearScreen &&
        !liveStreaming.streamingEnded &&
        !liveStreaming.isOpeningChannel && (
          <LiveStreamingControls
            isMute={liveStreaming.isMute}
            isCameraOff={liveStreaming.isCameraOff}
            cameraCount={liveStreaming.cameraCount}
            onToggleMute={liveStreaming.toggleMute}
            onToggleCamera={liveStreaming.toggleCamera}
            onSwitchCamera={liveStreaming.switchCamera}
            onEndLive={() => void confirmThenEndLive()}
          />
        )}

      {/* Token-renewal overlay (Flutter `renewingTokenWidget` :867). */}
      {liveStreaming.isRenewingToken && (
        <LiveStreamingStateViews variant="renewingToken" />
      )}

      {/* Streaming-ended overlay (Flutter `streamingEndedWidget` :868). */}
      {liveStreaming.streamingEnded && (
        <LiveStreamingStateViews variant="streamingEnded" />
      )}

      {/* Top-bar band — a single app-bar-style row so the back arrow (leading)
          and the watcher pill (trailing) are vertically centered in a
          `topBar.height` band and share one right line at `spacing.sm` (the
          same content row `ExpauseAppBar` builds — this overlay bar stays
          hand-rolled because it floats transparently over full-bleed video
          rather than sitting in flow). The back arrow is always present, NOT
          clear-screen gated (Flutter Scaffold `appBar()` leading
          `appBarLeading()` `:1058-1076`, back → `onBackPress()` `:282-285`); the
          watcher pill keeps its live-surface gate (Flutter `_watchersStats`
          gated `!_clearScreen && !_liveStreamingEnded && !_isOpeningChannel`,
          `:869-871`). An empty right slot holds the back arrow left-anchored
          under `space-between` when the pill is hidden. */}
      <div
        style={{
          position: 'absolute',
          top: responsive.safeArea.top,
          left: 0,
          right: 0,
          height: responsive.topBar.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: responsive.spacing.sm,
          paddingRight: responsive.spacing.sm,
          boxSizing: 'border-box',
          zIndex: 10,
        }}
      >
        <BackIconButton
          onClick={() => void handleBack()}
          qa={{ id: 'live-streaming-back' }}
        />

        {!liveStreaming.clearScreen &&
        !liveStreaming.streamingEnded &&
        !liveStreaming.isOpeningChannel ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: responsive.spacing.sm,
            }}
          >
            {/* Flutter orders the app-bar stats [coins, watchers] (`liveStats()`,
                `:1086`), so the coins pill renders left of the watcher pill. */}
            <LiveStreamingCoinsStat coins={liveStreaming.coins} />
            <LiveStreamingWatchersStat watchers={liveStreaming.watchers} />
          </div>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
};

export const LiveStreamingPage: React.FC = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Canonical screen-key wiring — captured ONCE on mount (like VideoCallPage).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (the route args), taken ONCE at mount. `null` on
  // the deep-link / refresh path — renders the error view, never fetches by id.
  const routeArgs = useMemo<LiveStreamingRouteArgs | null>(() => {
    const state = location.state as LiveStreamingRouteArgs | undefined;
    if (
      state &&
      state.broadcasterFirebaseId &&
      state.broadcasterName &&
      state.liveStreamingMessageId
    ) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Agora client — built ONCE (memoized) so the provider holds a stable
  // instance across renders. `mode: "live"`, `codec: "vp8"`; the role
  // (host/audience) is set in the hook. Consumed via `useRTCClient()`.
  const client = useMemo<IAgoraRTCClient>(
    () => AgoraRTC.createClient({ mode: 'live', codec: 'vp8' }),
    [],
  );

  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100dvh',
    backgroundColor: theme.background,
  } as const;

  // Deep-link / refresh with no route args — surface the error, no by-id fetch
  // (anti-deep-link, like `MediaDetailsPage` / `VideoCallPage`). Terminal.
  if (!routeArgs) {
    return (
      <div
        {...qaAttr({ id: 'live-streaming-page', status: 'error' })}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.base,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: responsive.safeArea.top,
            left: responsive.spacing.sm,
          }}
        >
          <BackIconButton
            onClick={() => Navigator.goBack()}
            qa={{ id: 'live-streaming-back' }}
          />
        </div>
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            textAlign: 'center',
          }}
        >
          {l10n.community.liveStreamingPage.argsMissingError}
        </span>
      </div>
    );
  }

  // Auth-hydrate gate (args present, current user not yet resolved) — transient,
  // so it spins rather than erroring. The hook reads `currentUser` for
  // `isBroadcaster`, so the body is held until resolved.
  if (!currentUser) {
    return (
      <div
        {...qaAttr({ id: 'live-streaming-page', status: 'loading' })}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  // The provider must wrap the body so `useLiveStreaming` (`useRTCClient()`) + the
  // video view resolve the client.
  return (
    <AgoraRTCProvider client={client}>
      <LiveStreamingPageBody
        args={routeArgs}
        screenKey={screenKey}
        currentUserFirebaseId={currentUser.firebaseId}
      />
    </AgoraRTCProvider>
  );
};
